import React, { useState, useEffect, useContext } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Rating from "@mui/material/Rating";
import Chip from "@mui/material/Chip";
import Badge from "@mui/material/Badge";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import { postRecord } from "services/services";
import { AuthContext } from "context/Authentication";
import { ThemeContext } from "context/ThemeContext";
import SnackBar from "CommonComponents/Snackbar/SnackBar.jsx";
import ProductQuickView from "CommonComponents/QuickViews/ProductQuickView.jsx";
import ProductChoiceSelector from "CommonComponents/ProductChoiceSelector/ProductChoiceSelector.jsx";
import { addToGuestCart } from "utils/guestCartOperations";

const API_AddToCart = "cart/create";
const API_UpdateCart = "cart/update";
const API_GetCartByUserId = "cart/getbyuserid";
const API_AddToWishlist = "wishlist/create";
const API_DeleteWishlist = "wishlist/delete";
const API_GetWishlistByUserId = "wishlist/getbyuserid";

const BestSellersSection = ({
  products,
  loading,
  headerRefresh,
  setHeaderRefresh,
  sectionSettings,
}) => {
  const [wishlist, setWishlist] = useState([]);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackOptions, setSnackOptions] = useState({
    color: "success",
    message: "",
  });
  const [fade, setFade] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [showChoiceSelector, setShowChoiceSelector] = useState(false);
  const [selectedChoices, setSelectedChoices] = useState({});
  const [currentProduct, setCurrentProduct] = useState(null);
  const [units] = useState(1); // Default quantity for best sellers
  const context = useContext(AuthContext);
  const themeMode = useContext(ThemeContext);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    if (context.state.isAuthenticated) {
      fetchWishlist(isMounted);
    }

    return () => {
      isMounted = false;
    };
  }, [context.state.isAuthenticated]);

  // Smooth image shuffling effect with longer duration
  useEffect(() => {
    let isMounted = true;

    if (
      !products.length ||
      !products[0]?.Default_Media ||
      products[0].Default_Media.length <= 1
    ) {
      return;
    }

    const interval = setInterval(() => {
      if (!isMounted) {
        clearInterval(interval);
        return;
      }

      setFade(false);
      setTimeout(() => {
        if (!isMounted) return;

        // Cycle through images for the main product
        setCurrentImageIndex((prevIndex) =>
          prevIndex === products[0].Default_Media.length - 1 ? 0 : prevIndex + 1
        );

        setTimeout(() => {
          if (!isMounted) return;
          setFade(true);
        }, 200); // Smooth fade-in delay
      }, 800); // Longer fade-out duration
    }, 5000); // Increased interval duration for best sellers

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [products.length, products[0]?.Default_Media?.length]);

  const fetchWishlist = async (mountedState) => {
    try {
      const response = await postRecord(API_GetWishlistByUserId, {
        User_Id: context.state.user.User_Id,
      });
      if (mountedState && response.status === 200 && response.data) {
        setWishlist(response.data);
      }
    } catch (error) {
      if (mountedState) {
        console.log(error);
      }
    }
  };

  const handleApiResponse = (
    response,
    successMessage,
    resetHeaderRefresh = false
  ) => {
    if (response.status === 200) {
      setSnackOptions({ color: "success", message: successMessage });
      setSnackOpen(true);
      if (resetHeaderRefresh) setHeaderRefresh(true);
    } else {
      setSnackOptions({ color: "error", message: "Something went wrong!" });
      setSnackOpen(true);
    }
  };

  const handleApiError = (error) => {
    console.error(error);
    setSnackOptions({ color: "error", message: "An error occurred!" });
    setSnackOpen(true);
  };

  const addToCart = async (product) => {
    setCurrentProduct(product);

    const hasChoices = hasChoiceOptions(product);
    const choicesSelected = Object.keys(selectedChoices).length > 0;

    // If product has choice options and they haven't been selected yet
    if (hasChoices && !choicesSelected) {
      setShowChoiceSelector(true);
      return;
    }

    // If product addons feature is enabled, check if this product actually has addons
    if (isProductAddonsEnabled()) {
      try {
        const availableAddons = await checkProductAddons(product);
        if (availableAddons && availableAddons.length > 0) {
          // BestSellersSection doesn't support addons, so just proceed with normal cart
        }
      } catch (error) {}
    }

    // Otherwise proceed with direct cart addition (no choices, no addons OR choices selected but no addons)
    // Pass product directly to avoid race condition with state update
    await proceedWithCartFlow(product);
  };

  const performDirectCartAdd = async (
    choiceOptions = [],
    forceCreateNew = false,
    productToAdd = null
  ) => {
    // Handle both authenticated and guest cart flows
    if (!context.state.isAuthenticated) {
      // Temporarily disable guest checkout
      setSnackOptions({
        color: "warning",
        message: "Please Login/Register to order"
      });
      setSnackOpen(true);
      
      setTimeout(() => {
        navigate('/Login');
      }, 1500);
      
      return;
      // await performGuestCartAdd(choiceOptions, productToAdd);
      // return;
    }

    // Continue with existing authenticated flow
    await performAuthenticatedCartAdd(
      choiceOptions,
      forceCreateNew,
      productToAdd
    );
  };

  const performGuestCartAdd = async (
    choiceOptions = [],
    productToAdd = null
  ) => {
    try {
      const product = productToAdd || currentProduct;
      if (!product) {
        handleApiError({ message: "Product not found" });
        return;
      }

      // Keep choiceOptions in object format for API compatibility
      const choiceOptionsForGuest = choiceOptions || {};

      // Prepare product details for guest cart - match ProductCard structure
      const productDetails = {
        // Core product info
        Product_Full_Name: product.Product_Full_Name,
        Short_Description: product.Short_Description || "",
        Product_Images: product.Product_Images || product.Default_Media || [],
        Default_Media: product.Default_Media || [],

        // Image handling - get first available image
        Product_Image_1: (() => {
          if (
            product.Product_Images &&
            Array.isArray(product.Product_Images) &&
            product.Product_Images.length > 0
          ) {
            return product.Product_Images[0];
          }
          if (
            product.Default_Media &&
            Array.isArray(product.Default_Media) &&
            product.Default_Media.length > 0
          ) {
            return product.Default_Media[0];
          }
          return null;
        })(),

        // Pricing and inventory
        Total_Selling_Price:
          product.Default_Inventory?.Total_Selling_Price || 0,
        Current_Stock: product.Default_Inventory?.Current_Stock || 0,

        // Combination details
        Combination_Id: product.Default_Combination?.Combination_Id,
        Combination_Text: product.Default_Combination?.Combination_Text || "",

        // Product status flags
        Is_Deleted: product.Is_Deleted || "0",
        Is_Soldout: product.Default_Combination?.Is_Soldout || "0",

        // Store full objects for compatibility
        Default_Inventory: product.Default_Inventory,
        Default_Combination: product.Default_Combination,
        Product: {
          Product_Full_Name: product.Product_Full_Name,
          Short_Description: product.Short_Description || "",
          Is_Deleted: product.Is_Deleted || "0",
        },
        Inventory: product.Default_Inventory,
        Combination: product.Default_Combination,
      };

      // Add to guest cart
      const success = await addToGuestCart({
        productId: product.Product_Id,
        quantity: units,
        choiceOptions: choiceOptionsForGuest,
        addons: [],
        productDetails,
        dispatch: context.dispatch,
        showSnackbar: (message, severity) => {
          setSnackOptions({
            color: severity,
            message: message,
          });
          setSnackOpen(true);
        },
      });

      if (success) {
        // Reset selections after successful add
        setSelectedChoices({});
        setHeaderRefresh?.(!headerRefresh);
      }
    } catch (error) {
      console.error("Error adding to guest cart:", error);
      setSnackOptions({
        color: "error",
        message: "Failed to add item to cart",
      });
      setSnackOpen(true);
    }
  };

  const performAuthenticatedCartAdd = async (
    choiceOptions = [],
    forceCreateNew = false,
    productToAdd = null
  ) => {
    // Use the passed product or fall back to currentProduct state
    const product = productToAdd || currentProduct;

    try {
      // Validate that product exists
      if (!product) {
        console.error("No product selected");
        handleApiError({ message: "Please select a product first" });
        return;
      }

      console.log("BestSellersSection - Product data:", {
        Product_Id: product.Product_Id,
        Default_Combination: product.Default_Combination,
        Default_Inventory: product.Default_Inventory,
      });

      // Validate required fields
      if (!product.Product_Id) {
        console.error("Product_Id is missing:", product);
        handleApiError({ message: "Product ID is required" });
        return;
      }

      if (!product.Default_Inventory?.Total_Selling_Price) {
        console.error("Price information is missing:", product);
        handleApiError({ message: "Product price information is required" });
        return;
      }

      // Skip existing item lookup when choice options are present or forceCreateNew is true
      const shouldCheckExisting =
        !forceCreateNew &&
        (!choiceOptions || Object.keys(choiceOptions).length === 0);
      let existingCartItem = null;

      console.log(
        "BestSellersSection - shouldCheckExisting:",
        shouldCheckExisting
      );

      if (shouldCheckExisting) {
        const cartResponse = await postRecord(API_GetCartByUserId, {
          User_Id: context.state.user.User_Id,
        });

        if (cartResponse.status === 200 && cartResponse.data) {
          existingCartItem = cartResponse.data.find(
            (item) =>
              item.Combination_Id === product.Default_Combination.Combination_Id
          );
        }
      }

      if (existingCartItem) {
        console.log(
          "BestSellersSection - Updating existing cart item:",
          existingCartItem
        );

        const updatedCartItem = {
          ...existingCartItem,
          Units: existingCartItem.Units + 1,
          Total_Price:
            (existingCartItem.Units + 1) * existingCartItem.Price_Per_Unit,
          Choice_Options:
            choiceOptions && Object.keys(choiceOptions).length > 0
              ? choiceOptions
              : null,
          Modified_By: context.state.user.User_Id,
        };

        const response = await postRecord(API_UpdateCart, updatedCartItem);
        handleApiResponse(response, "Item updated in cart!", true);
      } else {
        console.log("BestSellersSection - Creating new cart item");

        const newCartItem = {
          User_Id: context.state.user.User_Id,
          Product_Id: product.Product_Id,
          Combination_Id: product.Default_Combination.Combination_Id,
          Units: 1,
          Price_Per_Unit: product.Default_Inventory.Total_Selling_Price,
          Total_Price: product.Default_Inventory.Total_Selling_Price,
          Is_Savefor_Later: "0",
          Choice_Options:
            choiceOptions && Object.keys(choiceOptions).length > 0
              ? choiceOptions
              : null,
          Added_By: context.state.user.User_Id,
        };

        console.log("BestSellersSection - New cart item:", newCartItem);

        const response = await postRecord(API_AddToCart, newCartItem);
        handleApiResponse(response, "Item added to cart!", true);
      }
    } catch (error) {
      console.error(
        "BestSellersSection - Error in performDirectCartAdd:",
        error
      );
      handleApiError(error);
    }
  };

  const toggleWishlist = async (product) => {
    if (!context.state.isAuthenticated) {
      alert("Please log in to add items to the wishlist.");
      return;
    }

    const isInWishlist = wishlist.some(
      (item) => item.Product_Id === product.Product_Id
    );

    try {
      if (isInWishlist) {
        const wishlistItem = wishlist.find(
          (item) => item.Product_Id === product.Product_Id
        );

        const response = await postRecord(API_DeleteWishlist, {
          Wishlist_Id: wishlistItem.Wishlist_Id,
          Deleted_By: context.state.user.User_Id,
        });
        handleApiResponse(response, "Item removed from wishlist!", true);
        fetchWishlist(true);
      } else {
        const newWishlistItem = {
          Wishlist_Name: "My Wishlist",
          User_Id: context.state.user.User_Id,
          Product_Id: product.Product_Id,
          Combination_Id: product?.Default_Combination?.Combination_Id,
          Added_By: context.state.user.User_Id,
        };

        const response = await postRecord(API_AddToWishlist, newWishlistItem);
        handleApiResponse(response, "Item added to wishlist!", true);
        fetchWishlist(true);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const isInWishlist = (product) =>
    wishlist.some((item) => item.Product_Id === product.Product_Id);

  // Helper functions for choice options and addons
  const hasChoiceOptions = (product) => {
    if (!product?.Choice_Options) return false;
    try {
      let parsed;
      if (typeof product.Choice_Options === "string") {
        parsed = JSON.parse(product.Choice_Options);
      } else {
        parsed = product.Choice_Options;
      }

      // Check for new nested format: [{name: 'Size', options: ['S', 'M', 'L']}]
      if (Array.isArray(parsed)) {
        return (
          parsed.length > 0 &&
          parsed.some(
            (choice) =>
              choice.name &&
              Array.isArray(choice.options) &&
              choice.options.length > 0
          )
        );
      }

      return false;
    } catch (error) {
      console.error("Error parsing choice options:", error);
      return false;
    }
  };

  // Check if Product Addons feature is enabled in app settings
  const isProductAddonsEnabled = () => {
    if (!context.state.appSettings) return false;
    const addonSetting = context.state.appSettings.find(
      (setting) =>
        setting.Setting_Name === "Product Addons" &&
        setting.Setting_Value === "Enabled" &&
        setting.Is_Active === "1"
    );
    return !!addonSetting;
  };

  // Check if product has available addons
  const checkProductAddons = async (product) => {
    try {
      const response = await postRecord(
        "product-addons/getbyproductid-enhanced",
        {
          Product_Id: product.Product_Id,
          Combination_Id: product.Default_Combination.Combination_Id,
        }
      );

      if (
        response.status === 200 &&
        response.data &&
        response.data.length > 0
      ) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.log("No addons found for this product/combination");
      return null;
    }
  };

  const handleChoicesSelected = (choices) => {
    setSelectedChoices(choices);
    setShowChoiceSelector(false);

    // After choices are selected, proceed directly to cart with choice options
    const choiceOptions =
      choices && Object.keys(choices).length > 0 ? choices : [];
    const forceCreateNew = true; // Always create new item when choices are selected
    performDirectCartAdd(choiceOptions, forceCreateNew, currentProduct);
  };

  const proceedWithCartFlow = async (product) => {
    // For normal add to cart (no choices), pass empty array and false for forceCreateNew
    const choiceOptions =
      Object.keys(selectedChoices).length > 0 ? selectedChoices : [];
    const forceCreateNew = Object.keys(selectedChoices).length > 0;

    // Pass product directly to performDirectCartAdd to avoid race condition
    await performDirectCartAdd(choiceOptions, forceCreateNew, product);
  };

  // Helper function to reset choices and close dialogs
  const resetAndPreview = () => {
    setSelectedChoices({});
    setShowChoiceSelector(false);
  };

  // Helper function to handle QuickView
  const handleQuickView = (e, product) => {
    e.stopPropagation();
    setQuickViewProduct(product);
    setQuickViewOpen(true);
  };

  // Helper function to get image URL safely
  const getImageUrl = (product, index = 0) => {
    if (product?.Default_Media && product.Default_Media[index]) {
      const media = product.Default_Media[index];
      const imagePath = media.Media_File_Path || media;
      return imagePath ? `${process.env.BASEURL}${imagePath}` : "";
    }
    return "";
  };

  // Helper function to calculate average rating - matches ProductCard.jsx logic
  const getAverageRating = (product) => {
    return product?.averageRating || 0;
  };

  // Helper function to get review count - matches ProductCard.jsx logic
  const getReviewCount = (product) => {
    return product?.reviewCount || 0;
  };

  return (
    <React.Fragment>
      <Container maxWidth="xl">
        <Box
          sx={{
            position: "relative",
            py: { xs: 2, md: 3 },
            px: { xs: 1, md: 2 },
            textAlign: "center",
          }}
        >
          {/* Section Heading from CMS */}
          {(sectionSettings?.customTitle ||
            sectionSettings?.showTitle !== false) && (
            <>
              <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                {sectionSettings?.customTitle || "Best Sellers"}
              </Typography>
              <Box
                sx={{
                  width: "80px",
                  height: "2px",
                  backgroundColor: "primary.main",
                  mx: "auto",
                  mb: 2,
                }}
              />
            </>
          )}

          {/* Section Sub Heading from CMS */}
          {sectionSettings?.customSubHeading && (
            <Typography
              variant="h6"
              sx={{ color: "text.secondary", mb: 1, textAlign: "center" }}
            >
              {sectionSettings.customSubHeading}
            </Typography>
          )}

          {/* Section Sub Text from CMS */}
          {sectionSettings?.customSubText && (
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mb: 2,
                maxWidth: "600px",
                mx: "auto",
                textAlign: "center",
              }}
            >
              {sectionSettings.customSubText}
            </Typography>
          )}

          <Grid container spacing={2}>
            {loading ? (
              Array.from(new Array(4)).map((_, index) => (
                <Grid item xs={12} sm={6} md={6} lg={3} key={index}>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={300}
                    animation="wave"
                  />
                </Grid>
              ))
            ) : (
              <>
                {products.length > 0 && (
                  <Grid item xs={12} sm={12} md={6} lg={6}>
                    <Card
                      sx={{
                        position: "relative",
                        overflow: "hidden",
                        height: { xs: 400, sm: 450, md: "75vh" },
                        minHeight: { xs: 350, sm: 400, md: 480 },
                        cursor: "pointer",
                        borderRadius: { xs: "8px", md: "12px" },
                        boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                        transition: "transform 0.4s ease, box-shadow 0.4s ease",
                        "&:hover": {
                          transform: "scale(1.01)",
                          boxShadow: "0 12px 35px rgba(0,0,0,0.25)",
                        },
                        "&:hover .product-info": {
                          minHeight: { xs: "42%", md: "42%" },
                          background: (theme) =>
                            theme.palette.mode === "dark"
                              ? "linear-gradient(to top, rgba(0,0,0,0.98) 0%, rgba(0,0,0,0.92) 40%, rgba(0,0,0,0.40) 75%, rgba(0,0,0,0.00) 100%)"
                              : "linear-gradient(to top, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 40%, rgba(255,255,255,0.40) 75%, rgba(255,255,255,0.00) 100%)",
                        },
                        "&:hover .product-description": {
                          opacity: 1,
                          maxHeight: { xs: "60px", md: "100px" },
                        },
                      }}
                      onClick={() => {
                        // Use the actual Combination_Text from the default combination
                        const combinationText = products[0]?.Default_Combination
                          ?.Combination_Text
                          ? products[0].Default_Combination.Combination_Text.toLowerCase().replace(
                              /[,\s]+/g,
                              "-"
                            ) // Replace commas and spaces with hyphens
                          : "default";

                        const combinationId =
                          products[0]?.Default_Combination?.Combination_Id ||
                          "";

                        navigate(
                          encodeURI(
                            `/ProductView/${products[0].Product_Full_Name.toLowerCase()}/${combinationText}/${combinationId}`
                          )
                        );
                      }}
                    >
                      {context.state?.companyInfo?.Is_Aggregator === "1" &&
                        products[0]?.Vendor_Name && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 16,
                              right: 16,
                              zIndex: 10,
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              color: "white",
                              padding: "6px 14px",
                              borderRadius: "30px",
                              backdropFilter: "blur(8px)",
                              border: "1px solid",
                              borderColor: "primary.main",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                              transition: "transform 0.3s ease",
                              "&:hover": {
                                transform: "translateY(-3px)",
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "bold" }}
                            >
                              {products[0].Vendor_Name}
                            </Typography>
                          </Box>
                        )}

                      {/* Discount badge for main card */}
                      {products[0]?.Default_Inventory?.Total_MRP_Price &&
                        products[0].Default_Inventory.Total_MRP_Price !==
                          products[0]?.Default_Inventory
                            ?.Total_Selling_Price && (
                          <Box
                            sx={{
                              position: "absolute",
                              top:
                                context.state?.companyInfo?.Is_Aggregator ===
                                  "1" && products[0]?.Vendor_Name
                                  ? 80
                                  : 16,
                              right: 16,
                              zIndex: 10,
                              backgroundColor: "rgba(255, 87, 34, 0.95)",
                              color: "white",
                              padding: "8px 12px",
                              borderRadius: "20px",
                              fontSize: "0.85rem",
                              fontWeight: "bold",
                              backdropFilter: "blur(8px)",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                            }}
                          >
                            {Math.round(
                              ((products[0].Default_Inventory.Total_MRP_Price -
                                products[0].Default_Inventory
                                  .Total_Selling_Price) /
                                products[0].Default_Inventory.Total_MRP_Price) *
                                100
                            )}
                            % OFF
                          </Box>
                        )}

                      <Box
                        component="img"
                        src={getImageUrl(products[0], currentImageIndex)}
                        alt={products[0]?.Product_Full_Name || "Product Image"}
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          opacity: fade ? 1 : 0,
                          transition:
                            "opacity 1s cubic-bezier(0.4, 0, 0.2, 1), transform 0.8s ease",
                          transform: "scale(1)",
                          "&:hover": {
                            transform: "scale(1.03)",
                          },
                        }}
                      />

                      {/* QuickView and Wishlist floating actions - top left */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 16,
                          left: 16,
                          zIndex: 10,
                          display: "flex",
                          flexDirection: "row",
                          gap: 1,
                        }}
                      >
                        <Tooltip title="Quick View" arrow placement="right">
                          <IconButton
                            size="medium"
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(0, 0, 0, 0.7)"
                                  : "rgba(255, 255, 255, 0.9)",
                              color: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.95)"
                                  : "rgba(0, 0, 0, 0.8)",
                              backdropFilter: "blur(15px)",
                              border: "1px solid",
                              borderColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.15)"
                                  : "rgba(0, 0, 0, 0.1)",
                              boxShadow: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "0 8px 32px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                                  : "0 8px 32px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
                              "&:hover": {
                                backgroundColor: "primary.main",
                                color: "white",
                                transform: "translateY(-3px) scale(1.1)",
                                borderColor: "primary.main",
                                boxShadow: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "0 12px 40px rgba(0, 0, 0, 0.7), 0 0 25px rgba(25, 118, 210, 0.5)"
                                    : "0 12px 40px rgba(25, 118, 210, 0.3), 0 0 25px rgba(25, 118, 210, 0.3)",
                              },
                              transition:
                                "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                            }}
                            onClick={(e) => handleQuickView(e, products[0])}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            isInWishlist(products[0])
                              ? "Remove from Wishlist"
                              : "Add to Wishlist"
                          }
                          arrow
                          placement="right"
                        >
                          <IconButton
                            size="medium"
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(0, 0, 0, 0.7)"
                                  : "rgba(255, 255, 255, 0.9)",
                              color: isInWishlist(products[0])
                                ? "secondary.main"
                                : (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.95)"
                                      : "rgba(0, 0, 0, 0.8)",
                              backdropFilter: "blur(15px)",
                              border: "1px solid",
                              borderColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.15)"
                                  : "rgba(0, 0, 0, 0.1)",
                              boxShadow: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "0 8px 32px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                                  : "0 8px 32px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
                              "&:hover": {
                                backgroundColor: "secondary.main",
                                color: "white",
                                transform: "translateY(-3px) scale(1.1)",
                                borderColor: "secondary.main",
                                boxShadow: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "0 12px 40px rgba(0, 0, 0, 0.7), 0 0 25px rgba(156, 39, 176, 0.5)"
                                    : "0 12px 40px rgba(156, 39, 176, 0.3), 0 0 25px rgba(156, 39, 176, 0.3)",
                              },
                              transition:
                                "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleWishlist(products[0]);
                            }}
                          >
                            {isInWishlist(products[0]) ? (
                              <FavoriteIcon />
                            ) : (
                              <FavoriteBorderIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>

                      <Box
                        className="product-info"
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          width: "100%",
                          background: (theme) =>
                            theme.palette.mode === "dark"
                              ? "linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.80) 40%, rgba(0,0,0,0.40) 75%, rgba(0,0,0,0.00) 100%)"
                              : "linear-gradient(to top, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.80) 40%, rgba(255,255,255,0.40) 75%, rgba(255,255,255,0.00) 100%)",
                          color: (theme) =>
                            theme.palette.mode === "dark"
                              ? "white"
                              : "rgba(0,0,0,0.87)",
                          padding: { xs: "16px", sm: "20px", md: "24px" },
                          transition:
                            "all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                          height: "auto",
                          minHeight: "35%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-end",
                          "&:hover": {
                            minHeight: "42%",
                            padding: {
                              xs: "20px 16px",
                              sm: "24px 20px",
                              md: "28px 24px",
                            },
                            background: (theme) =>
                              theme.palette.mode === "dark"
                                ? "linear-gradient(to top, rgba(0,0,0,0.98) 0%, rgba(0,0,0,0.92) 40%, rgba(0,0,0,0.40) 75%, rgba(0,0,0,0.00) 100%)"
                                : "linear-gradient(to top, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 40%, rgba(255,255,255,0.40) 75%, rgba(255,255,255,0.00) 100%)",
                          },
                        }}
                      >
                        {/* Bottom Section - Left and Right Aligned */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            justifyContent: "space-between",
                            alignItems: { xs: "stretch", md: "flex-end" },
                            gap: { xs: 2, md: 3 },
                            width: "100%",
                          }}
                        >
                          {/* Left Section - Ratings, Product Name, Description */}
                          <Box
                            sx={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            {/* Rating - always visible on left */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                justifyContent: "flex-start",
                                mb: 0.5,
                              }}
                            >
                              <Rating
                                value={getAverageRating(products[0])}
                                readOnly
                                precision={0.1}
                                size="small"
                                sx={{
                                  "& .MuiRating-iconFilled": {
                                    color: "#ffc107",
                                    filter:
                                      "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                                  },
                                  "& .MuiRating-iconEmpty": {
                                    color: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "rgba(255, 255, 255, 0.3)"
                                        : "rgba(0, 0, 0, 0.3)",
                                  },
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.8)"
                                      : "rgba(0, 0, 0, 0.6)",
                                  fontSize: "0.85rem",
                                }}
                              >
                                {getAverageRating(products[0])} (
                                {getReviewCount(products[0])} reviews)
                              </Typography>
                            </Box>

                            {/* Product Name */}
                            <Typography
                              variant="h4"
                              sx={{
                                fontWeight: "bold",
                                mb: 0.5,
                                textAlign: "left",
                                fontSize: {
                                  xs: "1.2rem",
                                  sm: "1.5rem",
                                  md: "2rem",
                                },
                                lineHeight: { xs: 1.2, md: 1.3 },
                                textShadow: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "1px 1px 3px rgba(0,0,0,0.5)"
                                    : "1px 1px 3px rgba(255,255,255,0.5)",
                              }}
                            >
                              {products[0].Product_Full_Name}
                            </Typography>

                            {/* Description on hover - left aligned */}
                            <Typography
                              className="product-description"
                              variant="body1"
                              sx={{
                                lineHeight: 1.4,
                                textAlign: "left",
                                color: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.9)"
                                    : "rgba(0, 0, 0, 0.7)",
                                opacity: 0,
                                maxHeight: 0,
                                overflow: "hidden",
                                transition:
                                  "opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), max-height 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                                ".MuiBox-root:hover &": {
                                  opacity: 1,
                                  maxHeight: "60px",
                                },
                              }}
                            >
                              {products[0]?.Short_Description ||
                                "Discover our featured best seller and explore its unique features."}
                            </Typography>
                          </Box>

                          {/* Right Section - Combination, Pricing, Add to Cart */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              justifyContent: "flex-end",
                              gap: 1.5,
                              minWidth: { xs: "auto", md: "200px" },
                              alignSelf: { xs: "flex-end", md: "auto" },
                            }}
                          >
                            {/* Combination */}
                            <Typography
                              variant="body2"
                              sx={{
                                backgroundColor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.15)"
                                    : "rgba(0,0,0,0.15)",
                                color: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "white"
                                    : "rgba(0,0,0,0.87)",
                                padding: "3px 10px",
                                borderRadius: "15px",
                                fontSize: "0.85rem",
                              }}
                            >
                              {products[0]?.Default_Combination?.Combination ||
                                "Default"}
                            </Typography>

                            {/* Pricing Section - MRP and Selling Price in one row */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 2,
                                textAlign: "right",
                              }}
                            >
                              {products[0]?.Default_Inventory
                                ?.Total_MRP_Price &&
                                products[0].Default_Inventory
                                  .Total_MRP_Price !==
                                  products[0]?.Default_Inventory
                                    ?.Total_Selling_Price && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      textDecoration: "line-through",
                                      color: (theme) =>
                                        theme.palette.mode === "dark"
                                          ? "rgba(255, 255, 255, 0.6)"
                                          : "rgba(0, 0, 0, 0.6)",
                                      fontSize: "1.2rem",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        fontSize: "0.9rem",
                                        mr: 0.5,
                                        opacity: 0.8,
                                        fontWeight: "normal",
                                      }}
                                    >
                                      {context.state?.companyInfo?.Currency}
                                    </Box>
                                    {products[0].Default_Inventory.Total_MRP_Price?.toFixed(
                                      2
                                    )}
                                  </Typography>
                                )}
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: "bold",
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: {
                                    xs: "1.1rem",
                                    sm: "1.4rem",
                                    md: "1.8rem",
                                  },
                                  color: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "white"
                                      : "rgba(0,0,0,0.87)",
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: {
                                      xs: "0.8rem",
                                      sm: "0.9rem",
                                      md: "1.1rem",
                                    },
                                    mr: 0.5,
                                    opacity: 0.8,
                                    fontWeight: "normal",
                                  }}
                                >
                                  {context.state?.companyInfo?.Currency}
                                </Box>
                                {products[0]?.Default_Inventory?.Total_Selling_Price?.toFixed(
                                  2
                                ) || "N/A"}
                              </Typography>
                            </Box>

                            {/* Add to Cart Button */}
                            <Tooltip title="Add to Cart" arrow placement="top">
                              <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<AddShoppingCartIcon />}
                                sx={{
                                  borderRadius: "30px",
                                  px: { xs: 2, md: 3 },
                                  py: { xs: 0.8, md: 1 },
                                  fontSize: { xs: "0.8rem", md: "1rem" },
                                  fontWeight: "bold",
                                  backgroundColor: "primary.main",
                                  "&:hover": {
                                    backgroundColor: "primary.dark",
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 5px 15px rgba(0,0,0,0.2)",
                                  },
                                  transition: "all 0.3s ease",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addToCart(products[0]);
                                }}
                              >
                                {hasChoiceOptions(products[0]) &&
                                (currentProduct?.Product_Id !==
                                  products[0]?.Product_Id ||
                                  Object.keys(selectedChoices).length === 0)
                                  ? "Select Options"
                                  : "Add to Cart"}
                              </Button>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                )}

                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <Grid container spacing={2}>
                    {products.slice(1, 5).map((item, index) => (
                      <Grid item xs={6} sm={6} md={6} lg={6} key={index}>
                        <Card
                          sx={{
                            position: "relative",
                            overflow: "hidden",
                            height: {
                              xs: 250,
                              sm: 220,
                              md: "calc(75vh / 2.1)",
                            },
                            minHeight: { xs: 220, sm: 200, md: 220 },
                            borderRadius: { xs: "6px", md: "8px" },
                            cursor: "pointer",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                            transition:
                              "transform 0.4s ease, box-shadow 0.4s ease",
                            "&:hover": {
                              transform: "scale(1.01)",
                              boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                            },
                            "&:hover .product-info-small": {
                              minHeight: { xs: "45%", md: "42%" },
                              background: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "linear-gradient(to top, rgba(0,0,0,0.98) 0%, rgba(0,0,0,0.92) 40%, rgba(0,0,0,0.40) 75%, rgba(0,0,0,0.00) 100%)"
                                  : "linear-gradient(to top, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 40%, rgba(255,255,255,0.40) 75%, rgba(255,255,255,0.00) 100%)",
                            },
                          }}
                          onClick={() => {
                            // Use the actual Combination_Text from the default combination
                            const combinationText = item?.Default_Combination
                              ?.Combination_Text
                              ? item.Default_Combination.Combination_Text.toLowerCase().replace(
                                  /[,\s]+/g,
                                  "-"
                                ) // Replace commas and spaces with hyphens
                              : "default";

                            const combinationId =
                              item?.Default_Combination?.Combination_Id || "";

                            navigate(
                              encodeURI(
                                `/ProductView/${item.Product_Full_Name.toLowerCase()}/${combinationText}/${combinationId}`
                              )
                            );
                          }}
                        >
                          {context.state?.companyInfo?.Is_Aggregator === "1" &&
                            item?.Vendor_Name && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  zIndex: 10,
                                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                                  color: "white",
                                  padding: "3px 8px",
                                  borderRadius: "20px",
                                  backdropFilter: "blur(4px)",
                                  border: "1px solid",
                                  borderColor: "primary.light",
                                  maxWidth: "70%",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.65rem",
                                    fontWeight: "bold",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "block",
                                  }}
                                >
                                  {item.Vendor_Name}
                                </Typography>
                              </Box>
                            )}

                          {/* Discount badge */}
                          {item?.Default_Inventory?.Total_MRP_Price &&
                            item.Default_Inventory.Total_MRP_Price !==
                              item?.Default_Inventory?.Total_Selling_Price && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top:
                                    context.state?.companyInfo
                                      ?.Is_Aggregator === "1" &&
                                    item?.Vendor_Name
                                      ? 40
                                      : 8,
                                  right: 8,
                                  zIndex: 10,
                                  backgroundColor: "rgba(255, 87, 34, 0.9)",
                                  color: "white",
                                  padding: "4px 8px",
                                  borderRadius: "12px",
                                  fontSize: "0.7rem",
                                  fontWeight: "bold",
                                  backdropFilter: "blur(4px)",
                                }}
                              >
                                {Math.round(
                                  ((item.Default_Inventory.Total_MRP_Price -
                                    item.Default_Inventory
                                      .Total_Selling_Price) /
                                    item.Default_Inventory.Total_MRP_Price) *
                                    100
                                )}
                                % OFF
                              </Box>
                            )}

                          <Box
                            component="img"
                            src={getImageUrl(item, 0)}
                            alt={item.Product_Full_Name || "Product Image"}
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              opacity: fade ? 1 : 0,
                              transition:
                                "opacity 0.5s ease-in-out, transform 0.5s ease",
                              transform: "scale(1)",
                              "&:hover": {
                                transform: "scale(1.03)",
                              },
                            }}
                          />

                          {/* QuickView and Wishlist floating actions - top left */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              zIndex: 10,
                              display: "flex",
                              flexDirection: "row",
                              gap: 0.5,
                            }}
                          >
                            <Tooltip title="Quick View" arrow placement="right">
                              <IconButton
                                size="small"
                                sx={{
                                  backgroundColor: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(0, 0, 0, 0.7)"
                                      : "rgba(255, 255, 255, 0.9)",
                                  color: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.95)"
                                      : "rgba(0, 0, 0, 0.8)",
                                  backdropFilter: "blur(12px)",
                                  border: "1px solid",
                                  borderColor: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.15)"
                                      : "rgba(0, 0, 0, 0.1)",
                                  boxShadow: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "0 4px 20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                                      : "0 4px 20px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
                                  "&:hover": {
                                    backgroundColor: "primary.main",
                                    color: "white",
                                    transform: "translateY(-2px) scale(1.1)",
                                    borderColor: "primary.main",
                                    boxShadow: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "0 8px 25px rgba(0,0,0,0.7), 0 0 20px rgba(25, 118, 210, 0.4)"
                                        : "0 8px 25px rgba(25, 118, 210, 0.3), 0 0 20px rgba(25, 118, 210, 0.2)",
                                  },
                                  transition:
                                    "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                                }}
                                onClick={(e) => handleQuickView(e, item)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                isInWishlist(item)
                                  ? "Remove from Wishlist"
                                  : "Add to Wishlist"
                              }
                              arrow
                              placement="right"
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  backgroundColor: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(0, 0, 0, 0.7)"
                                      : "rgba(255, 255, 255, 0.9)",
                                  color: isInWishlist(item)
                                    ? "secondary.main"
                                    : (theme) =>
                                        theme.palette.mode === "dark"
                                          ? "rgba(255, 255, 255, 0.95)"
                                          : "rgba(0, 0, 0, 0.8)",
                                  backdropFilter: "blur(12px)",
                                  border: "1px solid",
                                  borderColor: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.15)"
                                      : "rgba(0, 0, 0, 0.1)",
                                  boxShadow: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "0 4px 20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                                      : "0 4px 20px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
                                  "&:hover": {
                                    backgroundColor: "secondary.main",
                                    color: "white",
                                    transform: "translateY(-2px) scale(1.1)",
                                    borderColor: "secondary.main",
                                    boxShadow: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "0 8px 25px rgba(0,0,0,0.7), 0 0 20px rgba(156, 39, 176, 0.4)"
                                        : "0 8px 25px rgba(156, 39, 176, 0.3), 0 0 20px rgba(156, 39, 176, 0.2)",
                                  },
                                  transition:
                                    "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleWishlist(item);
                                }}
                              >
                                {isInWishlist(item) ? (
                                  <FavoriteIcon fontSize="small" />
                                ) : (
                                  <FavoriteBorderIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>

                          <Box
                            className="product-info-small"
                            sx={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              width: "100%",
                              background: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.80) 40%, rgba(0,0,0,0.40) 75%, rgba(0,0,0,0.00) 100%)"
                                  : "linear-gradient(to top, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.80) 40%, rgba(255,255,255,0.40) 75%, rgba(255,255,255,0.00) 100%)",
                              color: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "white"
                                  : "rgba(0,0,0,0.87)",
                              padding: {
                                xs: "10px 8px",
                                sm: "10px",
                                md: "12px",
                              },
                              transition:
                                "all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                              height: "auto",
                              minHeight: { xs: "40%", md: "35%" },
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "flex-end",
                              "&:hover": {
                                minHeight: { xs: "45%", md: "42%" },
                                padding: {
                                  xs: "12px 8px",
                                  sm: "12px 10px",
                                  md: "14px 12px",
                                },
                                background: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "linear-gradient(to top, rgba(0,0,0,0.98) 0%, rgba(0,0,0,0.92) 40%, rgba(0,0,0,0.40) 75%, rgba(0,0,0,0.00) 100%)"
                                    : "linear-gradient(to top, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 40%, rgba(255,255,255,0.40) 75%, rgba(255,255,255,0.00) 100%)",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                justifyContent: "space-between",
                                alignItems: {
                                  xs: "flex-start",
                                  md: "flex-end",
                                },
                                gap: { xs: 1, md: 1.5 },
                              }}
                            >
                              {/* Left Section - Ratings, Product Name */}
                              <Box
                                sx={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-start",
                                }}
                              >
                                {/* Rating - always visible on left */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    justifyContent: "flex-start",
                                    mb: 0.25,
                                  }}
                                >
                                  <Rating
                                    value={getAverageRating(item)}
                                    readOnly
                                    precision={0.1}
                                    size="small"
                                    sx={{
                                      fontSize: "0.7rem",
                                      "& .MuiRating-iconFilled": {
                                        color: "#ffc107",
                                        filter:
                                          "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
                                      },
                                      "& .MuiRating-iconEmpty": {
                                        color: (theme) =>
                                          theme.palette.mode === "dark"
                                            ? "rgba(255, 255, 255, 0.3)"
                                            : "rgba(0, 0, 0, 0.3)",
                                      },
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: (theme) =>
                                        theme.palette.mode === "dark"
                                          ? "rgba(255, 255, 255, 0.8)"
                                          : "rgba(0, 0, 0, 0.6)",
                                      fontSize: "0.6rem",
                                    }}
                                  >
                                    {getAverageRating(item)} (
                                    {getReviewCount(item)})
                                  </Typography>
                                </Box>

                                {/* Product Name */}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: "bold",
                                    fontSize: "0.8rem",
                                    lineHeight: 1.2,
                                    mb: 0.25,
                                    textAlign: "left",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    textShadow: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "1px 1px 2px rgba(0,0,0,0.5)"
                                        : "1px 1px 2px rgba(255,255,255,0.5)",
                                  }}
                                >
                                  {item.Product_Full_Name}
                                </Typography>
                              </Box>

                              {/* Right Section - Combination, Pricing, Add to Cart */}
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: { xs: "row", md: "column" },
                                  alignItems: { xs: "center", md: "flex-end" },
                                  justifyContent: {
                                    xs: "space-between",
                                    md: "flex-end",
                                  },
                                  gap: { xs: 1, md: 0.75 },
                                  width: { xs: "100%", md: "auto" },
                                }}
                              >
                                {/* Combination */}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    backgroundColor: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.15)"
                                        : "rgba(0,0,0,0.15)",
                                    color: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "white"
                                        : "rgba(0,0,0,0.87)",
                                    padding: "2px 6px",
                                    borderRadius: "8px",
                                    fontSize: "0.6rem",
                                    display: { xs: "none", md: "block" },
                                  }}
                                >
                                  {item?.Default_Combination?.Combination ||
                                    "Default"}
                                </Typography>

                                {/* Pricing Section - MRP and Selling Price in one row */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: { xs: 0.5, md: 1 },
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {item?.Default_Inventory?.Total_MRP_Price &&
                                    item.Default_Inventory.Total_MRP_Price !==
                                      item?.Default_Inventory
                                        ?.Total_Selling_Price && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          textDecoration: "line-through",
                                          color: (theme) =>
                                            theme.palette.mode === "dark"
                                              ? "rgba(255, 255, 255, 0.6)"
                                              : "rgba(0, 0, 0, 0.6)",
                                          fontSize: "0.7rem",
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Box
                                          component="span"
                                          sx={{
                                            fontSize: "0.6rem",
                                            mr: 0.25,
                                            opacity: 0.8,
                                            fontWeight: "normal",
                                          }}
                                        >
                                          {context.state?.companyInfo?.Currency}
                                        </Box>
                                        {item.Default_Inventory.Total_MRP_Price?.toFixed(
                                          2
                                        )}
                                      </Typography>
                                    )}
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: "bold",
                                      display: "flex",
                                      alignItems: "center",
                                      fontSize: "0.9rem",
                                      color: (theme) =>
                                        theme.palette.mode === "dark"
                                          ? "white"
                                          : "rgba(0,0,0,0.87)",
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        fontSize: "0.65rem",
                                        mr: 0.25,
                                        opacity: 0.8,
                                        fontWeight: "normal",
                                      }}
                                    >
                                      {context.state?.companyInfo?.Currency}
                                    </Box>
                                    {item?.Default_Inventory?.Total_Selling_Price?.toFixed(
                                      2
                                    ) || "N/A"}
                                  </Typography>
                                </Box>

                                {/* Add to Cart Icon Button */}
                                <Tooltip
                                  title={
                                    hasChoiceOptions(item) &&
                                    (currentProduct?.Product_Id !==
                                      item?.Product_Id ||
                                      Object.keys(selectedChoices).length === 0)
                                      ? "Select Options"
                                      : "Add to Cart"
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <IconButton
                                    size="small"
                                    sx={{
                                      backgroundColor: (theme) =>
                                        theme.palette.mode === "dark"
                                          ? "rgba(0, 0, 0, 0.7)"
                                          : "rgba(255, 255, 255, 0.9)",
                                      color: "primary.main",
                                      backdropFilter: "blur(8px)",
                                      border: "1px solid",
                                      borderColor: (theme) =>
                                        theme.palette.mode === "dark"
                                          ? "rgba(255, 255, 255, 0.15)"
                                          : "rgba(0, 0, 0, 0.1)",
                                      "&:hover": {
                                        backgroundColor: "primary.main",
                                        color: "white",
                                        transform:
                                          "translateY(-1px) scale(1.1)",
                                        borderColor: "primary.main",
                                      },
                                      transition: "all 0.3s ease",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      addToCart(item);
                                    }}
                                  >
                                    <AddShoppingCartIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Container>

      {/* ProductQuickView Component */}
      <ProductQuickView
        open={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
        product={quickViewProduct}
        context={context}
        wishlist={wishlist}
        onAddToCart={addToCart}
        onToggleWishlist={toggleWishlist}
        getImageUrl={getImageUrl}
        getAverageRating={getAverageRating}
        getReviewCount={getReviewCount}
      />

      <SnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        options={snackOptions}
      />

      {/* Product Choice Options Selector Dialog */}
      <ProductChoiceSelector
        open={showChoiceSelector}
        onClose={() => setShowChoiceSelector(false)}
        product={currentProduct}
        selectedCombination={currentProduct?.Default_Combination}
        units={units}
        onChoicesSelected={handleChoicesSelected}
        onAddToCart={performDirectCartAdd}
        resetAndPreview={resetAndPreview}
        isProductAddonsEnabled={isProductAddonsEnabled}
        checkProductAddons={checkProductAddons}
        setParentSelectedChoices={setSelectedChoices}
      />
    </React.Fragment>
  );
};

export default BestSellersSection;
